import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { queryClient } from '@/queries/query-client';
import { useAuthStore } from '@/stores/auth.store';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Layout } from '@/components/layout/Layout';
import { Toaster } from '@/components/ui/sonner';
import { UserEventProvider } from '@/contexts/UserEventContext';
import { LanguageSettingsProvider } from '@/contexts/LanguageSettingsContext';
import { MissionNotificationListener } from '@/hooks/use-mission-notifications';
import { serviceWorkerRegistration } from '@/services/service-worker-registration.service';
import { backgroundSaveService } from '@/services/background-save.service';
import { saveQueueService } from '@/services/save-queue.service';
import { authStorageService } from '@/services/auth-storage.service';
import { PageLoadingFallback } from '@/components/ui/page-loading-fallback';
import { RouteSeo } from '@/components/seo/RouteSeo';

// ============================================================================
// ============================================================================

const PublicLanding = lazy(() => import('@/pages/PublicLanding'));
const MarketingResourcePage = lazy(() => import('@/pages/MarketingResourcePage'));
const AppPricingPage = lazy(() => import('@/pages/AppPricingPage'));
const CareerWorkspacePage = lazy(() => import('@/pages/CareerTodayPage'));
const SuccessBoardPage = lazy(() => import('@/pages/SuccessBoardPage'));
const Login = lazy(() => import('@/pages/Auth/Login'));
const SignUp = lazy(() => import('@/pages/Auth/SignUp'));
const OAuthCallback = lazy(() => import('@/pages/Auth/OAuthCallback'));
const CompleteProfile = lazy(() => import('@/pages/Auth/CompleteProfile'));

const Profile = lazy(() => import('@/pages/Profile'));
const CareerPortfolioPage = lazy(() => import('@/pages/CareerPortfolioPage'));
const CareerDashboardPage = lazy(() => import('@/pages/CareerDashboardPage'));
const PolicyPage = lazy(() => import('@/pages/PolicyPage'));
const LegacyCareerTreeRedirect = lazy(() => import('@/pages/LegacyCareerTreeRedirect'));

export default function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const isInitialized = useAuthStore((state) => state.isInitialized);

  useEffect(() => {
    // Initialize auth state on app load
    initialize();

    initAutoSaveSystem();

    // Cleanup
    return () => {
      backgroundSaveService.stop();
    };
  }, [initialize]);

  /**
   */
  async function initAutoSaveSystem() {
    try {
      console.log('🚀 Starting auto-save system initialization...');

      await saveQueueService.init();

      await serviceWorkerRegistration.register();

      await authStorageService.syncToken();

      backgroundSaveService.start();

      console.log('✅ Auto-save system initialized');
    } catch (error) {
      console.error('❌ Auto-save system initialization failed:', error);
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <RouteSeo />
        <LanguageSettingsProvider>
          <UserEventProvider>
            <MissionNotificationListener />
              <Toaster position="top-center" richColors />
              <Suspense fallback={<PageLoadingFallback />}>
                <Routes>
                  <Route element={<Layout />}>
                    {/* Public routes */}
                    <Route
                      path="/"
                      element={
                        !isInitialized ? (
                          <PageLoadingFallback />
                        ) : (
                          <PublicLanding />
                        )
                      }
                    />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/auth/callback" element={<OAuthCallback />} />
                    <Route path="/complete-profile" element={<CompleteProfile />} />
                    <Route path="/career/portfolio/:proofId" element={<CareerPortfolioPage />} />
                    <Route path="/resources/:slug" element={<MarketingResourcePage />} />
                    <Route path="/success-board" element={<SuccessBoardPage />} />
                    <Route path="/pricing" element={<PublicLanding />} />
                    <Route path="/terms" element={<PolicyPage />} />
                    <Route path="/privacy" element={<PolicyPage />} />
                    <Route path="/refund" element={<PolicyPage />} />
                    <Route path="/support" element={<PolicyPage />} />
                    <Route path="/policy/:policyKey" element={<PolicyPage />} />

                    {/* Protected routes */}
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <Navigate to="/app/paths" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/app/pricing"
                      element={
                        <ProtectedRoute>
                          <AppPricingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/app/:workspaceStep"
                      element={
                        <ProtectedRoute>
                          <CareerWorkspacePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/profile"
                      element={
                        <ProtectedRoute>
                          <Profile />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/*"
                      element={<LegacyCareerTreeRedirect source="Knowledge Board" target="/app/paths" targetLabel="CareerTree" />}
                    />
                    <Route
                      path="/profile/persona"
                      element={<LegacyCareerTreeRedirect source="Persona settings" target="/profile" targetLabel="My Account" />}
                    />
                    <Route
                      path="/profile/missions"
                      element={<LegacyCareerTreeRedirect source="Missions" target="/dashboard" targetLabel="My Growth Dashboard" />}
                    />
                    <Route
                      path="/timeline"
                      element={<LegacyCareerTreeRedirect source="Timeline" target="/dashboard" targetLabel="My Growth Dashboard" />}
                    />
                    <Route
                      path="/skills"
                      element={<LegacyCareerTreeRedirect source="Skills" target="/dashboard" targetLabel="My Growth Dashboard" />}
                    />
                    <Route
                      path="/publication/*"
                      element={<LegacyCareerTreeRedirect source="Publication" target="/dashboard" targetLabel="My Growth Dashboard" />}
                    />

                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <CareerDashboardPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Catch all - redirect to home */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Route>
                </Routes>
              </Suspense>
          </UserEventProvider>
        </LanguageSettingsProvider>
      </BrowserRouter>
      {/* <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> */}
    </QueryClientProvider>
  );
}
